import { Stack, Typography, useTheme } from "@mui/material";
import { type FC, type ReactNode, useRef } from "react";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import {
	type InspirationCta,
	InspirationCtaButton,
} from "web_myplann/Inspiration/Components/InspirationCtaButton";
import { InspirationDetailHeader } from "web_myplann/Inspiration/Components/InspirationDetailHeader";
import { InspirationDetailHero } from "web_myplann/Inspiration/Components/InspirationDetailHero";
import { MyPlannLayout } from "web_myplann/Shared/Header/MyPlannLayout";

type Props = {
	image: string;
	category: string;
	title: string;
	subtitle: string;
	primaryCta?: InspirationCta;
	secondaryCta?: InspirationCta;
	disclaimer?: string;
	scrollResetKey?: string | number | boolean;
	children: ReactNode;
};

export const InspirationDetailLayout: FC<Props> = ({
	image,
	category,
	title,
	subtitle,
	primaryCta,
	secondaryCta,
	disclaimer,
	scrollResetKey,
	children,
}) => {
	const { t } = useResource();
	const theme = useTheme();

	const pageBg = theme.palette.surface.muted;

	const heroRef = useRef<HTMLDivElement>(null);

	return (
		<MyPlannLayout
			disableContentPadding
			bgColor={pageBg}
			topGlow
			scrollResetKey={scrollResetKey}
		>
			<InspirationDetailHeader heroRef={heroRef} title={title} />

			<Stack>
				<InspirationDetailHero
					ref={heroRef}
					image={image}
					category={category}
					title={title}
					subtitle={subtitle}
				/>

				{children}

				{(primaryCta || secondaryCta) && (
					<Stack pt={2.4} pb={2.4} px={1.6} gap={1.2}>
						{primaryCta && <InspirationCtaButton cta={primaryCta} />}
						{secondaryCta && <InspirationCtaButton cta={secondaryCta} />}
					</Stack>
				)}

				<Typography
					variant="hint"
					textAlign="center"
					lineHeight={1.625}
					px={1.6}
					pb={2.4}
				>
					{disclaimer ?? t(Resources.Inspiration.disclaimer)}
				</Typography>
			</Stack>
		</MyPlannLayout>
	);
};
